import { useId, useMemo, useRef, useState } from "react";

import { labelPositions } from "@/lib/chart-axis";
import { lineSegments } from "@/lib/line-segments";

export type LineSeries = {
  label?: string;
  /** `null` — величины в этот день нет: линия прерывается, точка не
   *  рисуется, подсказка пишет «—». Ряд без единого числа не рисуется
   *  вовсе и не участвует в подписи оси. */
  data: (number | null)[];
};

/** Числа ряда — без `null` и без бесконечностей. */
function finiteValues(s: LineSeries): number[] {
  return s.data.filter((v): v is number => v != null && Number.isFinite(v));
}

type AxisSide = "left" | "right";

type LineChartProps = {
  series: LineSeries[];
  labels: string[];
  /** По индексу: подписать эту точку и выделить её. Пусто — прежнее
   *  поведение, подпись через одну. Задаётся встроенным графиком Ads
   *  Manager: в недельном режиме отмечаются понедельники, в месячном —
   *  первые числа. */
  emphasis?: boolean[];
  height?: number;
  width?: number;
  colors?: string[];
  /** Per-series axis hint from the caller. Series sharing a side are
   *  scaled against the same max, so each side's peak fills ~90% of
   *  its own axis height regardless of absolute magnitude. When
   *  omitted the chart auto-groups by order of magnitude. */
  axes?: AxisSide[];
  formatValue?: (seriesIndex: number, value: number) => string;
  tooltipHeader?: (index: number) => string;
  /** Минимальный зазор между подписями оси. Задаётся вызывающим, потому
   *  что ширина подписи известна только ему: диапазон шире дня. */
  labelMinGap?: number;
};

/**
 * Multi-series line chart with automatic dual-axis assignment.
 *
 * Series are auto-grouped into "big" (left axis) and "small" (right
 * axis) by magnitude. If every series is within 10× of the biggest,
 * there is only one axis. Otherwise smaller-magnitude series move to
 * a second Y axis so their day-to-day variance stays visible instead
 * of collapsing onto the baseline.
 *
 * The right axis is intentionally padded so its peak sits below the
 * left axis's peak — this way "bigger number = higher line" holds
 * visually even though the scales differ.
 *
 * Each axis gets a vertical rotated title listing the metric(s) it
 * covers, so the user can tell at a glance what scale they're reading.
 */
export function LineChart({
  series,
  labels,
  emphasis,
  height = 260,
  width = 900,
  colors = ["var(--c-accent-600)", "#1f9d55"],
  axes,
  formatValue,
  tooltipHeader,
  labelMinGap,
}: LineChartProps) {
  const gradBase = useId();

  const { leftIdx, rightIdx, maxLeft, maxRight, sides } = useMemo(
    () => (axes ? buildFromAxes(series, axes) : assignAxes(series)),
    [series, axes]
  );
  // ★ Ось подписывается только рядами с данными; сторона без единой
  // линии с данными не выводится вовсе. Иначе пустая метрика ставила бы
  // ось под пустоту, а подпись «SPEND · SPEND» на левой оси и была
  // симптомом подмены.
  const hasData = series.map((s) => finiteValues(s).length > 0);
  const leftWithData = leftIdx.filter((i: number) => hasData[i]);
  const rightWithData = rightIdx.filter((i: number) => hasData[i]);
  const hasRight = rightWithData.length > 0;

  const pad = {
    l: 62,
    r: hasRight ? 62 : 18,
    t: 16,
    b: 28,
  };
  const w = width;
  const h = height;
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;

  const x = (i: number) => pad.l + (i / (labels.length - 1 || 1)) * innerW;

  // Правило подписей — в `@/lib/chart-axis`: там его можно проверить
  // числами, а не глазом.
  const labelAt = labelPositions(labels.length, x, labelMinGap);
  // Both axes use the full inner height — each series's peak fills
  // ~90% of its own axis regardless of absolute magnitude. $ metrics
  // share the left axis; counts/rates share the right.
  const y = (v: number, side: AxisSide) => {
    const max = side === "right" ? maxRight : maxLeft;
    return pad.t + innerH - (v / max) * innerH;
  };

  const tickCount = 4;
  const leftTicks = Array.from(
    { length: tickCount + 1 },
    (_, i) => (i / tickCount) * maxLeft
  );
  const rightTicks = Array.from(
    { length: tickCount + 1 },
    (_, i) => (i / tickCount) * maxRight
  );

  const wrapRef = useRef<HTMLDivElement>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  function handleMove(e: React.MouseEvent<HTMLDivElement>) {
    if (!wrapRef.current || labels.length === 0) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * w;
    const rel = (px - pad.l) / (innerW || 1);
    const i = Math.round(rel * (labels.length - 1));
    if (i < 0 || i >= labels.length) {
      setHoverIdx(null);
      return;
    }
    setHoverIdx(i);
  }

  const tooltipX = hoverIdx == null ? null : (x(hoverIdx) / w) * 100;
  const tooltipOnRight = tooltipX != null && tooltipX > 55;

  const leftLabelText = leftWithData
    .map((i: number) => series[i]?.label ?? "")
    .filter(Boolean)
    .join(" · ");
  const rightLabelText = rightWithData
    .map((i: number) => series[i]?.label ?? "")
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      ref={wrapRef}
      style={{ position: "relative", width: "100%" }}
      onMouseMove={handleMove}
      onMouseLeave={() => setHoverIdx(null)}
    >
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{ display: "block" }}>
        <defs>
          {series.map((_, si) => (
            <linearGradient
              key={si}
              id={`${gradBase}-${si}`}
              x1="0"
              x2="0"
              y1="0"
              y2="1"
            >
              <stop
                offset="0%"
                stopColor={colors[si % colors.length]}
                stopOpacity="0.18"
              />
              <stop
                offset="100%"
                stopColor={colors[si % colors.length]}
                stopOpacity="0"
              />
            </linearGradient>
          ))}
        </defs>

        {/* Grid lines — driven by the left axis. */}
        {leftTicks.map((v, i) => (
          <line
            key={i}
            x1={pad.l}
            x2={w - pad.r}
            y1={y(v, "left")}
            y2={y(v, "left")}
            stroke="var(--c-divider)"
            strokeDasharray={i === 0 ? "0" : "3 4"}
          />
        ))}

        {/* Left axis tick labels. */}
        {leftTicks.map((v, i) => (
          <text
            key={`l-${i}`}
            x={pad.l - 8}
            y={y(v, "left") + 3}
            textAnchor="end"
            fontSize="10.5"
            fill="var(--c-text-4)"
            fontFamily="var(--font-mono)"
          >
            {formatAxisValue(v)}
          </text>
        ))}

        {/* Right axis tick labels + dedicated top guide line. */}
        {hasRight && (
          <>
            {rightTicks.map((v, i) => (
              <text
                key={`r-${i}`}
                x={w - pad.r + 8}
                y={y(v, "right") + 3}
                textAnchor="start"
                fontSize="10.5"
                fill="var(--c-text-4)"
                fontFamily="var(--font-mono)"
              >
                {formatAxisValue(v)}
              </text>
            ))}
            {/* Subtle right-axis top marker so the user sees the right
             *  axis maxes out lower than the left axis's peak. */}
            <line
              x1={w - pad.r - 2}
              x2={w - pad.r + 2}
              y1={y(maxRight, "right")}
              y2={y(maxRight, "right")}
              stroke="var(--c-text-4)"
              strokeWidth="1"
            />
          </>
        )}

        {/* Vertical axis titles — rotated, centered along each axis. */}
        {leftLabelText && (
          <text
            transform={`rotate(-90 14 ${pad.t + innerH / 2})`}
            x={14}
            y={pad.t + innerH / 2}
            textAnchor="middle"
            fontSize="10"
            fontWeight="600"
            fill="var(--c-text-3)"
            style={{ letterSpacing: "0.08em", textTransform: "uppercase" }}
          >
            {leftLabelText}
          </text>
        )}
        {hasRight && rightLabelText && (
          <text
            transform={`rotate(-90 ${w - 14} ${pad.t + innerH / 2})`}
            x={w - 14}
            y={pad.t + innerH / 2}
            textAnchor="middle"
            fontSize="10"
            fontWeight="600"
            fill="var(--c-text-3)"
            style={{ letterSpacing: "0.08em", textTransform: "uppercase" }}
          >
            {rightLabelText}
          </text>
        )}

        {/* Подписи оси X.
            ★ Без `emphasis` — как раньше: через одну, чтобы на
            двухнедельном отрезке они не наезжали друг на друга.
            С `emphasis` подписывается ТОЛЬКО отмеченное — понедельники в
            недельном режиме, первые числа в месячном — и подписывается
            жирнее. Промежуточные дни при этом остаются делениями на оси:
            без них не видно, что маркер изменения стоит на конкретном
            дне, а не на всей неделе. */}
        {/* ★ Деления на оси — на КАЖДОЙ точке. Подписан не каждый день, и
            без делений не видно, что маркер изменения стоит на конкретном
            дне, а не «где-то в этом отрезке». */}
        {labels.map((_, i) => (
          <line
            key={`t${i}`}
            x1={x(i)}
            y1={h - pad.b}
            x2={x(i)}
            y2={h - pad.b + 4}
            stroke="var(--c-text-4)"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {labels.map((l, i) => {
          if (!labelAt[i]) return null;
          const marked = emphasis?.[i] ?? false;
          return (
            <text
              key={i}
              x={x(i)}
              y={h - 8}
              // ★ Крайние подписи прижимаются к своему краю: у центрированной
              // половина уходит за поле и обрезается. Особенно заметно на
              // агрегированном графике, где крайняя подпись — диапазон и
              // потому вдвое шире обычной.
              textAnchor={
                i === 0 ? "start" : i === labels.length - 1 ? "end" : "middle"
              }
              fontSize="10.5"
              fontWeight={marked ? 600 : undefined}
              fill={marked ? "var(--c-text-2)" : "var(--c-text-4)"}
            >
              {l}
            </text>
          );
        })}

        {series.map((s, si) => {
          if (!hasData[si]) return null;
          const side = sides[si];
          const color = colors[si % colors.length];
          const segs = lineSegments(s.data);
          const strokes = segs.map((seg) =>
            seg
              .map((i, k) => `${k === 0 ? "M" : "L"}${x(i)} ${y(s.data[i]!, side)}`)
              .join(" "),
          );
          const fills = segs.map(
            (seg, k) =>
              `${strokes[k]} L${x(seg[seg.length - 1]!)} ${y(0, side)} L${x(seg[0]!)} ${y(0, side)} Z`,
          );
          const lastIdx = segs[segs.length - 1]!.slice(-1)[0]!;
          const last = [x(lastIdx), y(s.data[lastIdx]!, side)] as const;
          return (
            <g key={si}>
              {fills.map((d, k) => (
                <path key={`f${k}`} d={d} fill={`url(#${gradBase}-${si})`} />
              ))}
              {strokes.map((d, k) => (
                <path key={`s${k}`} d={d} fill="none" stroke={color} strokeWidth="2" />
              ))}
              {hoverIdx == null && (
                <circle
                  cx={last[0]}
                  cy={last[1]}
                  r="4"
                  fill="#fff"
                  stroke={color}
                  strokeWidth="2"
                />
              )}
            </g>
          );
        })}

        {hoverIdx != null && (
          <g>
            <line
              x1={x(hoverIdx)}
              x2={x(hoverIdx)}
              y1={pad.t}
              y2={h - pad.b}
              stroke="var(--c-text-4)"
              strokeDasharray="3 3"
            />
            {series.map((s, si) => {
              const v = s.data[hoverIdx];
              if (v == null || !Number.isFinite(v)) return null;
              return (
                <circle
                  key={si}
                  cx={x(hoverIdx)}
                  cy={y(v, sides[si])}
                  r="4"
                  fill="#fff"
                  stroke={colors[si % colors.length]}
                  strokeWidth="2"
                />
              );
            })}
          </g>
        )}
      </svg>

      {hoverIdx != null && tooltipX != null && (
        <div
          style={{
            position: "absolute",
            top: 8,
            left: tooltipOnRight ? undefined : `${tooltipX}%`,
            right: tooltipOnRight ? `${100 - tooltipX}%` : undefined,
            transform: tooltipOnRight
              ? "translate(-12px, 0)"
              : "translate(12px, 0)",
            background: "var(--c-surface)",
            border: "1px solid var(--c-border)",
            borderRadius: 8,
            boxShadow: "var(--sh-3)",
            padding: "8px 10px",
            minWidth: 180,
            fontSize: 12,
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: 0.4,
              color: "var(--c-text-4)",
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            {tooltipHeader ? tooltipHeader(hoverIdx) : labels[hoverIdx]}
          </div>
          {series.map((s, si) => {
            const v = s.data[hoverIdx];
            return (
              <div
                key={si}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "2px 0",
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: colors[si % colors.length],
                    flex: "none",
                  }}
                />
                <span style={{ color: "var(--c-text-3)", flex: 1 }}>
                  {s.label ?? `Series ${si + 1}`}
                </span>
                <b
                  style={{
                    color: "var(--c-text)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {v == null || !Number.isFinite(v)
                    ? "—"
                    : formatValue
                    ? formatValue(si, v)
                    : v.toLocaleString()}
                </b>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

type Assignment = {
  sides: AxisSide[];
  leftIdx: number[];
  rightIdx: number[];
  maxLeft: number;
  maxRight: number;
};

/**
 * Group series by order of magnitude. The biggest series sets the
 * "primary" scale; series whose max is within 10× go to the left
 * axis with it, the rest drop to the right axis where their own
 * variance becomes visible.
 */
function assignAxes(series: readonly LineSeries[]): Assignment {
  const maxes = series.map((s) => {
    const m = Math.max(...finiteValues(s), 0);
    return m > 0 ? m : 1;
  });
  const globalMax = Math.max(...maxes, 1);
  const sides: AxisSide[] = maxes.map((m) => (m * 10 >= globalMax ? "left" : "right"));
  return buildAssignment(series, sides);
}

/** Caller-driven axis assignment — respects the `axes` prop supplied
 *  by Overview (e.g. split $ metrics from count metrics). */
function buildFromAxes(
  series: readonly LineSeries[],
  axes: readonly AxisSide[]
): Assignment {
  // Pad/truncate to match series length. Default extras to "left".
  const sides: AxisSide[] = series.map((_, i) => axes[i] ?? "left");
  return buildAssignment(series, sides);
}

function buildAssignment(
  series: readonly LineSeries[],
  sides: AxisSide[]
): Assignment {
  const maxes = series.map((s) => {
    const m = Math.max(...finiteValues(s), 0);
    return m > 0 ? m : 1;
  });
  const leftIdx: number[] = [];
  const rightIdx: number[] = [];
  sides.forEach((side, i) => {
    if (side === "left") leftIdx.push(i);
    else rightIdx.push(i);
  });
  const maxLeft = leftIdx.length
    ? Math.max(...leftIdx.map((i) => maxes[i]!), 1) * 1.1
    : 1;
  const maxRight = rightIdx.length
    ? Math.max(...rightIdx.map((i) => maxes[i]!), 1) * 1.1
    : 1;
  return { sides, leftIdx, rightIdx, maxLeft, maxRight };
}

function formatAxisValue(v: number): string {
  if (v >= 1000) return Math.round(v).toLocaleString();
  if (v >= 10) return v.toFixed(0);
  if (v >= 1) return v.toFixed(1);
  return v.toFixed(2);
}
